import React, { useEffect, useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useTenantProducts } from "@hooks/useTenantProducts";
import { MODULE_PRESENTATION } from "@pages/Launcher/catalog";
import { openProduct } from "@pages/Launcher/openProductStrategy";
import { Module } from "@pages/Launcher/types";
import { ERoutes } from "src/helpers/utils";

/**
 * Entry point for legacy product bookmarks. Accepts
 * `?product=<id|alias|label>` and an optional `?return=<encoded URL>`.
 * Resolves the requested product against the user's `tenantProducts` list
 * and, if the user has access, fires the active open-product strategy —
 * which under SSO mode redirects to `${productUrl}/auth/sso` with the B2C
 * token in the URL fragment.
 *
 * Unknown product, no access or no URL → redirects to the launcher so the
 * user lands somewhere useful instead of a blank page. Unauthenticated users
 * are handled upstream by `PrivateRoute`.
 */
const Launch: React.FC = () => {
  const [searchParams] = useSearchParams();
  const productKey = (searchParams.get("product") ?? "").toLowerCase();
  const returnTo = searchParams.get("return") ?? undefined;
  const { instance } = useMsal();
  const { products, loading } = useTenantProducts();

  // Resolve the requested product key (case-insensitive) against the catalog
  // — matching the backend alias ("TOPCON_TECH"), the frontend id ("tech")
  // or the visible label ("TECH"). All three are valid bookmark shapes.
  const targetModule = useMemo<Module | null>(() => {
    if (!productKey) return null;

    const match = Object.entries(MODULE_PRESENTATION).find(([alias, presentation]) => alias.toLowerCase() === productKey
      || presentation.id.toLowerCase() === productKey
      || presentation.label.toLowerCase() === productKey);

    if (!match) return null;

    const [alias, presentation] = match;
    const product = products.find((p) => p.module === alias);

    if (!product || !product.accessibleByCurrentUser || !product.baseUrl) return null;

    return {
      ...presentation,
      moduleId: product.moduleId,
      state: "accessible",
      url: product.baseUrl
    };
  }, [products, productKey]);

  useEffect(() => {
    if (loading || !targetModule) return;
    Promise.resolve(openProduct(targetModule, instance, { returnTo }))
      .catch(() => { /* strategy already falls back */ });
  }, [loading, targetModule, instance, returnTo]);

  // No product key, or product not resolvable: send the user to the launcher.
  if (!productKey || (!loading && !targetModule)) {
    return <Navigate to={`/${ERoutes.identity}`} replace />;
  }

  // While we're resolving / redirecting, render nothing — the page exists
  // only to redispatch the user.
  return null;
};

export default Launch;
